/**
 * Dutch -> English UI translations. Keys are the exact Dutch source strings
 * as they appear in the components (Dutch-string-as-key mechanism, see
 * ./language.tsx). Includes both literal t("...") keys and the Dutch values
 * of records/option arrays that are passed through t() as variables.
 */

export const EN: Record<string, string> = {
  // ---- App shell -----------------------------------------------------------
  "Quatt | All-E Recommender": "Quatt | All-E Recommender",
  "+ nieuwe aanbeveling": "+ new recommendation",
  "Lijstweergave": "List view",
  "Stap-voor-stap": "Step-by-step",
  "Weergave kiezen": "Choose view",
  "Niet gevonden": "Not found",
  "Niet gevonden.": "Not found.",
  "De pagina die je zoekt bestaat niet.": "The page you are looking for does not exist.",
  "Terug naar de aanbeveling": "Back to the recommendation",
  "Indicatief advies. Definitieve geschiktheid volgt na een installatie-inspectie.":
    "Indicative advice. Final suitability follows after an installation inspection.",

  // ---- Hero / page ---------------------------------------------------------
  "All-Electric geschiktheidscheck": "All-Electric suitability check",
  "All-e aanbeveling": "All-e recommendation",
  "All-E Recommender": "All-E Recommender",
  "Beantwoord een paar korte vragen over de woning. We doen meteen een passende aanbeveling: Quatt All-Electric Solo of Duo, of een Hybrid.":
    "Answer a few short questions about the home. We immediately give a fitting recommendation: Quatt All-Electric Solo or Duo, or a Hybrid.",
  "Welke heat pump past bij deze woning: All-Electric Solo of Duo? Vul in wat je weet en krijg meteen een aanbeveling plus aandachtspunten.":
    "Which heat pump fits this home: All-Electric Solo or Duo? Fill in what you know and get an instant recommendation plus points of attention.",
  "Kon de aanbeveling niet uitvoeren:": "Could not run the recommendation:",
  "Geen ID opgegeven.": "No ID provided.",
  "Laden...": "Loading...",
  "Kon resultaat niet laden:": "Could not load result:",

  // ---- Results page --------------------------------------------------------
  "Vergelijk de opties": "Compare the options",
  "Op basis van je gegevens volstaat een Solo. Wil je liever meer comfort, een hogere subsidie en een langere levensduur. Bekijk dan ook de Duo-upgrade.":
    "Based on your data, a Solo suffices. Prefer more comfort, a higher subsidy and a longer lifespan? Then also have a look at the Duo upgrade.",
  "Voldoende": "Sufficient",
  "Aanbevolen upgrade": "Recommended upgrade",
  "Eén buitenunit. Past binnen de berekende warmtevraag.": "One outdoor unit. Fits within the calculated heat demand.",
  "Twee buitenunits. Meer vermogen voor extra comfort.": "Two outdoor units. More capacity for extra comfort.",
  "Lagere investering": "Lower investment",
  "Kleinere voetafdruk buiten": "Smaller outdoor footprint",
  "Compact installatieproces": "Compact installation process",
  "Hogere subsidie": "Higher subsidy",
  "Meer vermogen, sneller op temperatuur": "More capacity, up to temperature faster",
  "Snellere warmwater-opwarming": "Faster hot-water heating",
  "Langere levensduur door minder belasting per unit": "Longer lifespan due to less load per unit",
  "All-Electric aanbeveling": "All-Electric recommendation",
  "All-Electric Duo (upgrade)": "All-Electric Duo (upgrade)",
  "Solo zou volstaan, maar Duo geeft meer vermogen, snellere warmwater en hogere subsidie.":
    "A Solo would suffice, but a Duo gives more capacity, faster hot water and a higher subsidy.",
  "Geschikt": "Suitable",
  "Extra controle": "Additional review",
  "Niet geschikt": "Not suitable",
  "Op basis van de ingevulde gegevens is deze woning geschikt voor een Quatt All-Electric installatie.":
    "Based on the data entered, this home is suitable for a Quatt All-Electric installation.",
  "Deze woning kan geschikt zijn, maar er zijn punten die extra aandacht nodig hebben. Bekijk de details hieronder.":
    "This home may be suitable, but there are points that need extra attention. See the details below.",
  "Op basis van de ingevulde gegevens is deze woning niet geschikt voor All-Electric. Een Quatt Hybrid is een passender configuratie, of de woning kan eerst aanvullend geïsoleerd worden.":
    "Based on the data entered, this home is not suitable for All-Electric. A Quatt Hybrid is a better fit, or the home can first receive additional insulation.",
  "All-Electric Solo": "All-Electric Solo",
  "All-Electric Duo": "All-Electric Duo",
  "Niet mogelijk": "Not possible",
  "Eén buitenunit volstaat voor de berekende warmtevraag bij -7°C.":
    "One outdoor unit suffices for the calculated heat demand at -7°C.",
  "De warmtevraag vraagt om een Duo-configuratie (twee buitenunits) bij -7°C.":
    "The heat demand calls for a Duo configuration (two outdoor units) at -7°C.",
  "Geen All-Electric configuratie haalt de warmtevraag bij -7°C. Eerst aanvullende isolatie of een Quatt Hybrid is een passender route.":
    "No All-Electric configuration meets the heat demand at -7°C. Additional insulation first, or a Quatt Hybrid, is a better route.",
  "Warmtevraag": "Heat demand",
  "Capaciteitsdekking": "Capacity coverage",
  "Aanbevolen pomp": "Recommended pump",
  "Max. binnentemp bij -7°C": "Max. indoor temp at -7°C",
  "Max. binnentemp": "Max. indoor temp",
  "Uitleg": "Explanation",
  "Uitleg tonen": "Show explanation",
  "Blokkeringen": "Blocking issues",
  "Per-check uitleg": "Per-check explanation",
  "Woningtype": "House type",
  "Warmtevraag dekking": "Heat demand coverage",
  "Watertemperatuur": "Water temperature",
  "Thermostaatgedrag": "Thermostat behaviour",
  "Isolatie & label": "Insulation & label",
  "Aandachtspunten": "Points of attention",
  "Volgende stap": "Next step",

  // ---- Unified tool: advies + hybride besparing ----------------------------
  "Advies": "Advice",
  "All-Electric": "All-Electric",
  "Hybride": "Hybrid",
  "Beste keuze: All-Electric": "Best choice: All-Electric",
  "Beste keuze: Quatt Hybrid": "Best choice: Quatt Hybrid",
  "Op basis van de ingevulde gegevens is deze woning geschikt voor All-Electric. Hieronder de aanbevolen configuratie, met de Hybride besparing als alternatief.":
    "Based on the entered data this home is suitable for All-Electric. Below the recommended configuration, with the Hybrid savings as an alternative.",
  "Op basis van de ingevulde gegevens is een Quatt Hybrid hier de passende keuze. Bekijk de geschatte besparing hieronder; All-Electric vraagt eerst extra aandacht.":
    "Based on the entered data a Quatt Hybrid is the fitting choice here. See the estimated savings below; All-Electric needs extra attention first.",
  "Geschatte besparing": "Estimated savings",
  "gas": "gas",
  "per jaar": "per year",
  "Hybride besparing": "Hybrid savings",
  "Quatt Hybrid Single": "Quatt Hybrid Single",
  "Quatt Hybrid Duo": "Quatt Hybrid Duo",
  "Geschatte jaarlijkse besparing met een Quatt Hybrid op basis van de ingevulde woninggegevens.":
    "Estimated yearly savings with a Quatt Hybrid based on the entered home data.",
  "Gasbesparing": "Gas savings",
  "CO₂-reductie": "CO₂ reduction",
  "Extra elektriciteit": "Extra electricity",
  "Vergelijk versies": "Compare versions",
  "Verberg versievergelijking": "Hide version comparison",
  "Quatt Hybrid V2": "Quatt Hybrid V2",
  "Quatt Hybrid V1.5": "Quatt Hybrid V1.5",
  "Solo": "Solo",
  "Duo": "Duo",

  "PDF kon niet gegenereerd worden:": "PDF could not be generated:",
  "Nieuwe aanbeveling": "New recommendation",
  "Of stuur de aanbeveling per e-mail": "Or send the recommendation by email",
  "naam@voorbeeld.nl": "name@example.com",
  "Versturen...": "Sending...",
  "Verstuur per e-mail": "Send by email",
  "Verstuurd naar": "Sent to",
  "Kon niet versturen:": "Could not send:",

  // Results STAT_HELP / CHECK_HELP
  "Jaarlijkse energievraag in kWh, afgeleid van gasverbruik of EP1.":
    "Yearly energy demand in kWh, derived from gas consumption or EP1.",
  "Hoeveel procent van de piekvraag bij -7 graden de geselecteerde pomp dekt.":
    "What percentage of the peak demand at -7 degrees the selected pump covers.",
  "Solo of Duo, op basis van warmtevraag en bestaand afgiftesysteem.":
    "Solo or Duo, based on heat demand and the existing emission system.",
  "Geschatte hoogste haalbare binnentemperatuur op een -7 graden dag.":
    "Estimated highest achievable indoor temperature on a -7 degree day.",
  "Blokkeert appartementen zonder VVE-toestemming en incompatibele heating-types (luchtverwarming, stadsverwarming).":
    "Blocks apartments without owners' association (VVE) approval and incompatible heating types (air heating, district heating).",
  "Vergelijkt jaarlijkse warmtevraag met capaciteit van Solo / Duo bij -7 graden buiten.":
    "Compares the yearly heat demand with Solo / Duo capacity at -7 degrees outside.",
  "Controleert of het afgiftesysteem voldoende vermogen levert op W35 / W45 / W55.":
    "Checks whether the emission system delivers enough capacity at W35 / W45 / W55.",
  "Zone-thermostaten en grote dag/nacht-setbacks zijn ongunstig voor een warmtepomp.":
    "Zone thermostats and large day/night setbacks are unfavourable for a heat pump.",
  "Bij energielabel D-G is All-Electric meestal niet geschikt zonder extra isolatie.":
    "With energy label D-G, All-Electric is usually not suitable without extra insulation.",

  // ---- Live sidebar --------------------------------------------------------
  "Live aanbeveling": "Live recommendation",
  "Vul de check in om de aanbeveling te zien": "Fill in the check to see the recommendation",
  "Isolatie": "Insulation",
  "Radiatoren": "Radiators",
  "OK": "OK",
  "CHECK": "CHECK",
  "BLOK": "BLOCK",
  "Aanbevolen": "Recommended",
  "Solo volstaat": "Solo suffices",
  "Duo nodig": "Duo needed",
  "Buiten bereik": "Out of range",
  "dekking bij -7°C": "coverage at -7°C",
  "Een buitenunit volstaat. De warmtevraag past binnen Solo-capaciteit.":
    "One outdoor unit suffices. The heat demand fits within Solo capacity.",
  "Twee buitenunits nodig om de piekvraag bij -7°C te halen. Solo is te krap.":
    "Two outdoor units needed to meet the peak demand at -7°C. Solo is too tight.",
  "Geen All-Electric configuratie haalt de warmtevraag. Een Quatt Hybrid of eerst aanvullende isolatie is een passender route.":
    "No All-Electric configuration meets the heat demand. A Quatt Hybrid, or additional insulation first, is a better route.",
  "De jaarlijkse energievraag voor verwarming in kWh, afgeleid van het gasverbruik (route Gas) of EP1 (route BENG1).":
    "The yearly energy demand for heating in kWh, derived from gas consumption (Gas route) or EP1 (BENG1 route).",
  "Hoeveel procent van de piekvraag bij -7 graden buiten de geselecteerde Quatt-pomp dekt. >=100% = ruim voldoende.":
    "What percentage of the peak demand at -7 degrees outside the selected Quatt pump covers. >=100% = amply sufficient.",
  "Quatt All-Electric Solo of Duo, gekozen op basis van warmtevraag, bestaand afgiftesysteem en aanvoertemperatuur (W35 / W45 / W55).":
    "Quatt All-Electric Solo or Duo, chosen based on heat demand, existing emission system and supply temperature (W35 / W45 / W55).",
  "De geschatte hoogst haalbare binnentemperatuur op een -7 graden dag bij dekking <100%. Lager dan 20 graden = comfortrisico.":
    "The estimated highest achievable indoor temperature on a -7 degree day at <100% coverage. Below 20 degrees = comfort risk.",
  "Blokkeert appartementen zonder VVE-toestemming of zonder buitenruimte, en heating-types die niet samenwerken met All-Electric (luchtverwarming, stadsverwarming).":
    "Blocks apartments without owners' association (VVE) approval or outdoor space, and heating types that do not work with All-Electric (air heating, district heating).",
  "Vergelijkt de jaarlijkse warmtevraag met de capaciteit van Solo/Duo bij -7 graden. Bepaalt welk model wordt aanbevolen.":
    "Compares the yearly heat demand with Solo/Duo capacity at -7 degrees. Determines which model is recommended.",
  "Controleert of het afgiftesysteem voldoende vermogen levert op de lagere aanvoertemperatuur van de warmtepomp (W35 / W45 / W55).":
    "Checks whether the emission system delivers enough capacity at the heat pump's lower supply temperature (W35 / W45 / W55).",
  "Zone-thermostaten + grote dag/nacht-setbacks zijn ongunstig voor een warmtepomp die het beste werkt met &eacute;&eacute;n constante regeling.":
    "Zone thermostats + large day/night setbacks are unfavourable for a heat pump, which works best with one constant control.",
  "Bij energielabel D-G is All-Electric meestal niet geschikt zonder eerst extra isolatie. Bij B/C is het een aandachtspunt.":
    "With energy label D-G, All-Electric is usually not suitable without extra insulation first. With B/C it is a point of attention.",
  "Controleert of het totale radiatorvermogen bij W55 voldoende is voor het ingevulde deel van de woning. alleen relevant bij hoge CV-aanvoer (>60 graden) en radiator-systeem.":
    "Checks whether the total radiator capacity at W55 is sufficient for the entered part of the home. Only relevant with a hot boiler supply (>60 degrees) and a radiator system.",

  // ---- Option labels (shared form arrays) ----------------------------------
  "Vloerverwarming": "Underfloor heating",
  "Vloerverwarming + andere bronnen": "Underfloor heating + other sources",
  "Vloerconvectoren": "Floor convectors",
  "Luchtverwarming": "Air heating",
  "Stadsverwarming": "District heating",
  "Vrijstaand": "Detached",
  "2-onder-1-kap": "Semi-detached",
  "Tussenwoning": "Mid-terrace",
  "Hoekwoning": "End-of-terrace",
  "Appartement": "Apartment",
  "Aan alle kanten vrij": "Free on all sides",
  "Aan één zijde geschakeld": "Attached on one side",
  "Tussen twee buren in": "Between two neighbours",
  "Op de hoek van de rij": "On the corner of the row",
  "Etagewoning of flat": "Flat or apartment building",
  "Voor 1970": "Before 1970",
  "1970-1990": "1970-1990",
  "1990-2005": "1990-2005",
  "Na 2005": "After 2005",
  "Spouwmuurisolatie": "Cavity wall insulation",
  "Dakisolatie": "Roof insulation",
  "Vloerisolatie": "Floor insulation",
  "HR++ of triple glas": "HR++ or triple glazing",
  "Nieuw < 5 jr": "New < 5 yrs",
  "Gemiddeld 5-15 jr": "Average 5-15 yrs",
  "Oud 15-25 jr": "Old 15-25 yrs",
  "Zeer oud 25+ jr": "Very old 25+ yrs",
  "Weinig": "Low",
  "Gemiddeld": "Average",
  "Veel": "High",
  "Goed": "Good",
  "Slecht": "Poor",
  "Niet bekend": "Unknown",
  "Onbekend": "Unknown",
  "Niet opgegeven": "Not specified",
  "Ja": "Yes",
  "Nee": "No",

  // ---- Form: address -------------------------------------------------------
  "Adres (optioneel)": "Address (optional)",
  "Vul postcode + huisnummer in om straat en plaats automatisch op te halen.":
    "Enter postcode + house number to automatically retrieve street and city.",
  "Postcode": "Postcode",
  "Huisnummer": "House number",
  "Toevoeging": "Suffix",
  "Adres opzoeken": "Look up address",
  "Bezig...": "Working...",
  "Vul postcode en huisnummer in.": "Enter postcode and house number.",
  "Ongeldige postcode (verwacht: 1234AB).": "Invalid postcode (expected: 1234AB).",
  "Onverwacht antwoord van de server.": "Unexpected response from the server.",
  "Adres-lookup is op deze omgeving niet beschikbaar": "Address lookup is not available in this environment",
  "Adres-lookup faalde": "Address lookup failed",
  "Onbekende fout.": "Unknown error.",
  "Sla over als je geen adresgegevens wilt invullen.": "Skip if you do not want to enter address details.",
  "Adres": "Address",

  // ---- Form: section 1 -----------------------------------------------------
  "Woningtype & uitsluiting": "House type & exclusion",
  "Uitleg woningtype": "Explanation house type",
  "Bepaalt of een Quatt All-Electric warmtepomp (Solo of Duo) past bij dit woningtype. Appartementen zijn alleen geschikt als de VVE toestemming geeft en er buitenruimte is voor de buitenunit. Selecteer ook het bestaande verwarmingstype. Luchtverwarming en stadsverwarming zijn niet compatibel met All-Electric.":
    "Determines whether a Quatt All-Electric heat pump (Solo or Duo) fits this house type. Apartments are only suitable if the owners' association (VVE) approves and there is outdoor space for the outdoor unit. Also select the existing heating type. Air heating and district heating are not compatible with All-Electric.",
  "Verwarmingstype:": "Heating type:",
  "meerdere selecties mogelijk. Een woning met vloerverwarming én radiatoren telt mee als gemengd systeem.":
    "multiple selections possible. A home with underfloor heating and radiators counts as a mixed system.",
  "VVE-toestemming verkregen?": "Owners' association (VVE) approval obtained?",
  "Buitenruimte beschikbaar voor buitenunit?": "Outdoor space available for the outdoor unit?",
  "Buitenruimte beschikbaar voor de buitenunit?": "Outdoor space available for the outdoor unit?",
  "Verwarmingstype (meerdere mogelijk)": "Heating type (multiple possible)",
  "Let op: niet compatibel met All-Electric.": "Note: not compatible with All-Electric.",

  // ---- Form: section 2 -----------------------------------------------------
  "Berekeningsmethode": "Calculation method",
  "Uitleg berekeningsmethode": "Explanation calculation method",
  "We berekenen de jaarlijkse warmtevraag via gasverbruik of via EP1 (energieprestatie). Gasverbruik is meestal nauwkeuriger. EP1 is een goede fallback wanneer de jaarnota niet beschikbaar is.":
    "We calculate the yearly heat demand from gas consumption or from EP1 (energy performance). Gas consumption is usually more accurate. EP1 is a good fallback when the annual statement is not available.",
  "Totaal gasverbruik": "Total gas consumption",
  "Te vinden op de jaarnota. Gebruik het meest recente volledige jaar vóór installatie van een hybride. Bij een lange wintervakantie (>2 weken): gebruik het gemiddelde van de niet-vakantiemaanden × 12.":
    "Found on the annual statement. Use the most recent full year before a hybrid was installed. For a long winter holiday (>2 weeks): use the average of the non-holiday months × 12.",
  "Gemiddeld zomerverbruik": "Average summer consumption",
  "Juni, juli en augustus van de jaarnota. In die maanden gaat gas alleen naar warm water en koken. Dit cijfer × 12 wordt afgetrokken van het totaal om het verwarmingsdeel te isoleren.":
    "June, July and August from the annual statement. In those months gas only goes to hot water and cooking. This figure × 12 is subtracted from the total to isolate the heating share.",
  "Het veld": "The field",
  "Warmtebehoefte": "Heat demand",
  "(kWh/m²/jaar) op": "(kWh/m²/year) on",
  '(zoek op postcode + huisnummer, klik op "Detailgegevens"). Vul ook het thermisch oppervlak in.':
    '(search by postcode + house number, click "Detailgegevens"). Also enter the thermal floor area.',
  "Gasverbruik": "Gas consumption",
  "op basis van gasrekening": "based on the gas bill",
  "op basis van energieprestatie": "based on energy performance",
  "Jaarlijks gasverbruik (m³)": "Yearly gas consumption (m³)",
  "Gemiddelde over laatste 2-3 jaar": "Average over the last 2-3 years",
  "Zomergemiddelde (m³/maand)": "Summer average (m³/month)",
  "Jun-Aug gemiddelde (warm water)": "Jun-Aug average (hot water)",
  "BENG1 / EP1 waarde (kWh/m²)": "BENG1 / EP1 value (kWh/m²)",
  "Energieprestatie-indicator": "Energy performance indicator",
  "Gebruiksoppervlakte (m²)": "Floor area (m²)",
  "Bruto vloeroppervlak": "Gross floor area",
  "Bruto vloeroppervlak.": "Gross floor area.",
  "Gemiddelde over de laatste 2 tot 3 jaar.": "Average over the last 2 to 3 years.",
  "Gemiddelde van juni, juli, augustus (warm water).": "Average of June, July, August (hot water).",
  "Warmtebehoefte uit ep-online.nl.": "Heat demand from ep-online.nl.",

  // ---- Form: section 3 -----------------------------------------------------
  "Isolatie & woning": "Insulation & home",
  "Uitleg isolatie": "Explanation insulation",
  "Isolatiekwaliteit bepaalt de warmtebehoefte per m² en daarmee de capaciteit die een All-Electric warmtepomp moet leveren bij -7°C buiten. Een goed geïsoleerde woning heeft een lagere piekvraag en past sneller binnen Solo-capaciteit.":
    "Insulation quality determines the heat demand per m² and thereby the capacity an All-Electric heat pump must deliver at -7°C outside. A well-insulated home has a lower peak demand and fits within Solo capacity more easily.",
  "Energielabel": "Energy label",
  "Het energielabel staat op het energiecertificaat of is opzoekbaar via":
    "The energy label is on the energy certificate or can be looked up via",
  "(postcode + huisnummer).": "(postcode + house number).",
  "D of lager": "D or lower",
  "Bij twijfel: kies Gemiddeld.": "When in doubt: choose Average.",
  "Isolatiemaatregelen": "Insulation measures",
  "Vink aan welke maatregelen al uitgevoerd zijn. Bij D-G label maar wel HR++/triple glas + spouw + dak gecombineerd kan de woning alsnog geschikt zijn. Vooral als de CV-aanvoer comfortabel onder 60°C blijft.":
    "Tick which measures have already been carried out. With a D-G label but HR++/triple glazing + cavity + roof combined, the home may still be suitable. Especially if the boiler supply stays comfortably below 60°C.",
  "Bouwjaar": "Build year",
  "Energielabel (optioneel)": "Energy label (optional)",
  "Energielabel (indien bekend)": "Energy label (if known)",
  "Let op: bij dit label is All-Electric vaak ongeschikt.": "Note: with this label All-Electric is often unsuitable.",
  "Isolatiemaatregelen (meerdere mogelijk)": "Insulation measures (multiple possible)",
  "Welke isolatie is al uitgevoerd?": "Which insulation has already been carried out?",

  // ---- Form: section 4 -----------------------------------------------------
  "Verwarmingssysteem & temperatuur": "Heating system & temperature",
  "Uitleg verwarmingssysteem": "Explanation heating system",
  "Een warmtepomp levert water op een lagere temperatuur dan een CV-ketel. Deze stap bepaalt of het bestaande afgiftesysteem voldoende warmte kan leveren op die lagere temperatuur. En welke aanvoertemperatuur (W35 / W45 / W55) nodig is.":
    "A heat pump delivers water at a lower temperature than a gas boiler. This step determines whether the existing emission system can deliver enough heat at that lower temperature. And which supply temperature (W35 / W45 / W55) is needed.",
  "CV-aanvoertemperatuur": "Boiler supply temperature",
  'Kijk op het display van de CV-ketel ("aanvoer-" of "stooktemperatuur"). Geen display? Check de ketel-app of het installatieblad in de meterkast. Typische waarden: 60-80°C voor oudere systemen, 50-60°C voor nieuwere.':
    'Check the boiler display ("supply" or "heating temperature"). No display? Check the boiler app or the installation sheet in the meter cupboard. Typical values: 60-80°C for older systems, 50-60°C for newer ones.',
  "Hoe dit de warmtevraag beïnvloedt": "How this influences the heat demand",
  "Hoe lager de huidige CV-instelling, hoe lager de aanvoertemperatuur die de warmtepomp moet leveren. Op W35 levert de unit significant meer vermogen dan op W55. Een hoge CV-instelling met alleen radiatoren verhoogt de kans op een tekort op koude dagen.":
    "The lower the current boiler setting, the lower the supply temperature the heat pump needs to deliver. At W35 the unit delivers significantly more capacity than at W55. A high boiler setting with radiators only increases the risk of a shortfall on cold days.",
  "Verwarmingsdistributie": "Heat distribution",
  "Vloerverwarming werkt op 35-45°C en is altijd geschikt. Aanvoer hangt af van de pompgroep:":
    "Underfloor heating works at 35-45°C and is always suitable. Supply depends on the pump group:",
  "zonder mengpompgroep → W35": "without mixing pump group → W35",
  "met mengpompgroep → W45": "with mixing pump group → W45",
  "Radiatoren zijn meestal ook geschikt, maar bij een hoge CV-instelling (>60°C) wordt een radiatorcheck aanbevolen.":
    "Radiators are usually also suitable, but with a high boiler setting (>60°C) a radiator check is recommended.",
  "CV comfortabel op 55°C?": "Boiler comfortable at 55°C?",
  "CV comfortabel op 55°C": "Boiler comfortable at 55°C",
  "Belangrijk voor All-Electric. Bij Nee wordt de woning gemarkeerd als extra controle.":
    "Important for All-Electric. If No, the home is flagged for additional review.",
  "Stille modus 's nachts?": "Silent mode at night?",
  "Bij Ja rekenen we met lager vermogen 's nachts (stille modus, minder geluid voor de buren). Bij Nee gebruiken we het volle vermogen 24/7 - meer reserve, maar mogelijk meer geluid op koude nachten.":
    "If Yes, we calculate with lower capacity at night (silent mode, less noise for the neighbours). If No, we use full capacity 24/7 - more reserve, but possibly more noise on cold nights.",
  "Verwarmingssysteem hele woning": "Heating system whole house",
  "Hoofdruimte distributie": "Main room distribution",
  "Dagtemperatuur (°C)": "Day temperature (°C)",
  "Nachttemperatuur (°C)": "Night temperature (°C)",
  "Verschil:": "Difference:",
  "Grote setback, ongunstig voor warmtepomp.": "Large setback, unfavourable for a heat pump.",
  "Huidige CV-aanvoertemperatuur (°C)": "Current boiler supply temperature (°C)",
  "Stookt de woning typisch op 60 °C of hoger? Vul de waarde in voor een exactere controle. Bij lege invoer rekenen we met de keuze hierboven.":
    "Does the home typically heat at 60 °C or higher? Enter the value for a more exact check. If left empty we use the choice above.",
  "Tip:": "Tip:",
  "bij een CV-aanvoer > 60 °C op radiatoren verschijnt de Radiatorcheck (stap 5).":
    "with a boiler supply > 60 °C on radiators, the Radiator check appears (step 5).",
  "Verdeler met pomp aanwezig?": "Manifold with pump present?",
  "Uitleg verdeler met pomp": "Explanation manifold with pump",
  "Met pomp (gesloten / mengverdeler):": "With pump (closed / mixing manifold):",
  "mengt heet ketelwater met retourwater tot vloertemperatuur. Gebruikt bij traditionele CV-ketels of systemen met meer dan 6-7 groepen. Hogere aanvoertemperatuur vereist (W45).":
    "mixes hot boiler water with return water down to floor temperature. Used with traditional gas boilers or systems with more than 6-7 groups. Higher supply temperature required (W45).",
  "Zonder pomp (open / directe verdeler):": "Without pump (open / direct manifold):",
  "gebruikt de pomp van de warmtepomp zelf, geen actieve menging. Energiezuiniger, stiller, minder onderhoud. Standaardkeuze bij warmtepompen en goed geïsoleerde woningen (W35).":
    "uses the heat pump's own pump, no active mixing. More energy-efficient, quieter, less maintenance. The standard choice with heat pumps and well-insulated homes (W35).",
  "Bij vloerverwarming met verdeler-pomp rekent de check met W45, zonder pomp met W35.":
    "With underfloor heating with a manifold pump the check uses W45, without a pump W35.",
  "Zone-thermostaat aanwezig?": "Zone thermostat present?",
  "Uitleg zone-thermostaat": "Explanation zone thermostat",
  "Zone-thermostaten regelen de temperatuur per kamer of per zone (Tado, Honeywell Evohome, Anna). De warmtepomp-regeling zelf is voor hybride en All-Electric gelijk -- het is geen technische blokker.":
    "Zone thermostats control the temperature per room or per zone (Tado, Honeywell Evohome, Anna). The heat pump control itself is the same for hybrid and All-Electric -- it is not a technical blocker.",
  'Wel relevant voor All-E: er is minder backup-vermogen dan bij hybride, de marges zijn dunner, zone-regeling tilt in de praktijk de watertemperatuur omhoog (lagere COP) en maakt het systeem complexer en storingsgevoeliger. Bij Ja komt de woning op "extra controle" te staan, niet automatisch ongeschikt -- laat de installateur meekijken.':
    'Still relevant for All-E: there is less backup capacity than with a hybrid, the margins are thinner, zone control pushes the water temperature up in practice (lower COP) and makes the system more complex and fault-prone. If Yes, the home is marked for "additional review", not automatically unsuitable -- have the installer take a look.',
  "Niet blokkerend -- regelt voor hybride en All-E hetzelfde. Wel: minder backup-vermogen, hogere watertemperaturen (lagere COP), complexer systeem.":
    "Not blocking -- controls the same for hybrid and All-E. However: less backup capacity, higher water temperatures (lower COP), more complex system.",
  "Extra warmtebronnen (open haard, airco, IR-paneel)?": "Additional heat sources (fireplace, air conditioning, IR panel)?",
  "Uitleg extra warmtebronnen": "Explanation additional heat sources",
  "Extra warmtebronnen (open haard, airco met heat-modus, infraroodpaneel) leveren ondersteunende warmte op de koudste dagen. Daardoor mag de capaciteitsdekking soms iets lager liggen. De bewoner accepteert dat de warmtepomp niet alle vraag dekt en vult bij met de extra bron.":
    "Additional heat sources (fireplace, air conditioning with heat mode, infrared panel) provide supporting heat on the coldest days. As a result the capacity coverage may sometimes be slightly lower. The resident accepts that the heat pump does not cover all demand and tops up with the extra source.",
  "Bij": "If",
  "verlagen we de dekkingsdrempel licht. Bij": "we slightly lower the coverage threshold. If",
  "moet de warmtepomp de volledige piek bij -7°C zelf dragen.":
    "the heat pump must carry the full peak at -7°C on its own.",
  "Extra warmtebronnen kunnen de sizing beïnvloeden. Soms is een lagere dekking acceptabel.":
    "Additional heat sources can influence the sizing. Sometimes a lower coverage is acceptable.",
  "Tapwaterklasse (CW)": "Hot water class (CW)",
  "Uitleg CW-klasse": "Explanation CW class",
  'CW staat voor "comfortklasse warm water". Het geeft aan hoe snel de CV-ketel warm tapwater levert.':
    'CW stands for "hot water comfort class". It indicates how quickly the gas boiler delivers hot tap water.',
  "is standaard voor de meeste woningen (één of twee badkamers, normaal gebruik).":
    "is standard for most homes (one or two bathrooms, normal use).",
  "is voor grote woningen met meerdere badkamers tegelijk in gebruik.":
    "is for large homes with multiple bathrooms in use at the same time.",
  "De Quatt All-Electric warmtepomp is functioneel gelijkwaardig aan CW5. Heeft de klant bewust een CW6-ketel? Vraag dan of de reden daarvoor bij All-Electric ook geldt, of dat CW5-capaciteit in de praktijk volstaat.":
    "The Quatt All-Electric heat pump is functionally equivalent to CW5. Does the customer deliberately have a CW6 boiler? Then ask whether the reason also applies to All-Electric, or whether CW5 capacity suffices in practice.",
  "Waar te vinden:": "Where to find:",
  "op het typeplaatje van de CV-ketel, in de handleiding, of via de Mijnketel-portal. Geen klasse vermeld? Dan is het vrijwel altijd CW5.":
    "on the boiler's type plate, in the manual, or via the Mijnketel portal. No class listed? Then it is almost always CW5.",
  "Quatt All-Electric is vergelijkbaar met CW5. Bij CW6 controleren we of die piekvraag echt nodig is.":
    "Quatt All-Electric is comparable to CW5. With CW6 we check whether that peak demand is really needed.",

  // ---- Form: radiators -----------------------------------------------------
  "Uitleg radiatorcheck": "Explanation radiator check",
  "Radiatoren zijn genormeerd (EN 442) op 75°C aanvoer / 65°C retour / 20°C ruimte. Op 55°C aanvoer levert een radiator nog maar ~52% van zijn nominale vermogen. Deze check bepaalt of het totale radiatoroppervlak voldoende is bij de lagere aanvoertemperatuur van de warmtepomp.":
    "Radiators are rated (EN 442) at 75°C supply / 65°C return / 20°C room. At 55°C supply a radiator delivers only ~52% of its nominal capacity. This check determines whether the total radiator surface is sufficient at the heat pump's lower supply temperature.",
  "Radiatortypen bij W55 (W/m²)": "Radiator types at W55 (W/m²)",
  "enkel vlak paneel, geen lamellen: 270": "single flat panel, no fins: 270",
  "enkel met lamellen: 460": "single with fins: 460",
  "dubbel vlak: 536": "double flat: 536",
  "dubbel + 1 lamel: 663": "double + 1 fin: 663",
  "dubbel + 2 lamellen: 918": "double + 2 fins: 918",
  "driedubbel + 3 lamellen: 1326.": "triple + 3 fins: 1326.",
  "Het type staat op een sticker op de zijkant of achterkant van de radiator. herkenbaar aan het aantal panelen en lamelrijen.":
    "The type is on a sticker on the side or back of the radiator. Recognisable by the number of panels and fin rows.",
  "Dekkingspercentage": "Coverage percentage",
  "Vul alleen radiatoren in die normaal openstaan: woonkamer, keuken, slaapkamers. Sla kleine of zelden gebruikte ruimtes over. Het percentage geeft aan welk aandeel van de totale warmtevraag de ingevulde radiatoren moeten dragen.":
    "Only enter radiators that are normally open: living room, kitchen, bedrooms. Skip small or rarely used rooms. The percentage indicates what share of the total heat demand the entered radiators must carry.",
  "Bij een hoge CV-aanvoer controleren we of de bestaande radiatoren ook voldoende vermogen leveren bij W55 (de typische aanvoer van een warmtepomp). Vul de grootste radiatoren in. Klein of zelden gebruikte ruimtes mag je overslaan.":
    "With a hot boiler supply we check whether the existing radiators also deliver enough capacity at W55 (a heat pump's typical supply). Enter the largest radiators. You may skip small or rarely used rooms.",
  "Huidige CV-aanvoer:": "Current boiler supply:",
  "(zie stap 4).": "(see step 4).",
  "Ruimte": "Room",
  "Lengte (cm)": "Length (cm)",
  "Hoogte (cm)": "Height (cm)",
  "Vermogen (kW)": "Capacity (kW)",
  "Vermogen:": "Capacity:",
  "Actie": "Action",
  "Woonkamer": "Living room",
  "Radiator": "Radiator",
  "verwijderen": "remove",
  "Radiator toevoegen": "Add radiator",
  "Kies type": "Choose type",
  "Ingevuld aandeel van totale warmtevraag (%)": "Entered share of total heat demand (%)",
  "Welk deel van de woning heb je ingevuld? Standaard 70%.": "What part of the home did you enter? Default 70%.",
  "Totaal vermogen:": "Total capacity:",
  "Benodigd:": "Required:",
  "Dekking:": "Coverage:",
  "Radiatorcheck": "Radiator check",
  "Bij hoge CV-aanvoer kijken we of de radiatoren ook op W55 voldoende zijn.":
    "With a hot boiler supply we check whether the radiators are also sufficient at W55.",
  "Vul de grootste radiatoren in. Klein of zelden gebruikte ruimtes mag je overslaan.":
    "Enter the largest radiators. You may skip small or rarely used rooms.",

  // ---- Form: advanced ------------------------------------------------------
  "Voor preciezere aanbeveling": "For a more precise recommendation",
  "Bewoners, ketelleeftijd, warmwatergebruik en isolatiescores per element. Alleen invullen als bekend. Geeft een nauwkeurigere capaciteitsberekening (BENG2 + COP-cluster) en daarmee een scherpere Solo / Duo aanbeveling.":
    "Residents, boiler age, hot water use and insulation scores per element. Only fill in if known. Gives a more accurate capacity calculation (BENG2 + COP cluster) and thereby a sharper Solo / Duo recommendation.",
  "Aantal bewoners": "Number of residents",
  "Ketelleeftijd": "Boiler age",
  "Warmwatergebruik": "Hot water use",
  "Geschat elektriciteitsverbruik (kWh/jaar)": "Estimated electricity use (kWh/year)",
  "Isolatiescores per element (0-10, optioneel)": "Insulation scores per element (0-10, optional)",
  "10 = perfect, 5 = gemiddeld, 0 = geen isolatie.": "10 = perfect, 5 = average, 0 = no insulation.",
  "Dak": "Roof",
  "Wand": "Wall",
  "Vloer": "Floor",
  "Ramen": "Windows",
  "Bereken aanbeveling": "Calculate recommendation",

  // ---- Stepper -------------------------------------------------------------
  "Wat voor woning is het?": "What kind of home is it?",
  "Hoe wordt de woning nu verwarmd?": "How is the home currently heated?",
  "Meerdere antwoorden mogelijk.": "Multiple answers possible.",
  "Welke gegevens heb je bij de hand?": "Which data do you have at hand?",
  "Kies de input die je het makkelijkst kunt invullen.": "Choose the input that is easiest for you to fill in.",
  "Hoe goed is de woning geïsoleerd?": "How well is the home insulated?",
  "Hoe heet stookt de CV-ketel nu?": "How hot does the boiler currently run?",
  "Hoe wordt de warmte verdeeld?": "How is the heat distributed?",
  "Welke binnentemperaturen wil je halen?": "Which indoor temperatures do you want to reach?",
  "Een paar laatste vragen": "A few final questions",
  "Klaar voor je aanbeveling": "Ready for your recommendation",
  "Is de woning comfortabel op 55°C aanvoer?": "Is the home comfortable at 55°C supply?",
  "Belangrijk voor All-Electric. Bij Nee gaan we extra controleren.":
    "Important for All-Electric. If No, we will do an additional review.",
  "Huidige CV-aanvoer (°C, optioneel)": "Current boiler supply (°C, optional)",
  "Te vinden op het display van de CV-ketel.": "Found on the boiler display.",
  "Stille modus 's nachts gewenst?": "Silent mode at night desired?",
  "Hoofdruimte (woonkamer) distributie": "Main room (living room) distribution",
  "Met verdeler-pomp rekenen we met W45, zonder pomp met W35.":
    "With a manifold pump we use W45, without a pump W35.",
  "Aanbevolen: 20°C overdag.": "Recommended: 20°C during the day.",
  "Aanbevolen: 18-19°C 's nachts.": "Recommended: 18-19°C at night.",
  "Kleinere setback (≤ 4°C verschil) is gunstiger voor een warmtepomp.":
    "A smaller setback (≤ 4°C difference) is more favourable for a heat pump.",
  "Per-kamer thermostaten (Tado, Evohome, Anna) passen niet bij de Quatt warmtepomp.":
    "Per-room thermostats (Tado, Evohome, Anna) do not fit the Quatt heat pump.",
  "Extra warmtebronnen (haard, airco, IR-paneel)?": "Additional heat sources (fireplace, air conditioning, IR panel)?",
  "Verwarming nu": "Current heating",
  "Verdeling": "Distribution",
  "Dag- / nachttemp.": "Day / night temp.",
  "We hebben genoeg gegevens. Controleer hieronder je antwoorden en bekijk je aanbeveling.":
    "We have enough data. Check your answers below and view your recommendation.",
  "Geschikt voor All-Electric": "Suitable for All-Electric",
  "Mogelijk geschikt, extra controle": "Possibly suitable, additional review",
  "Op basis hiervan: niet geschikt": "Based on this: not suitable",
  "Aanbevolen pomp:": "Recommended pump:",
  "Dekking bij -7°C:": "Coverage at -7°C:",
  "Stap": "Step",
  "van": "of",
  "Terug": "Back",
  "Volgende": "Next",
  "Bekijk aanbeveling": "View recommendation",
};
